
import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import UseScrollAnimation from "../../hooks/UseScrollAnimation";
import { CartProduct } from "../../assets/icons/icons";
import axios from "axios";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/swiper-bundle.css";

const API_BASE = "https://laravel-api.aj-mart.com";

export default function ProductGridTwo() {
    const { t, i18n } = useTranslation();

    UseScrollAnimation();

    const isArabic = i18n.language?.startsWith("ar");

    const [filter, setFilter] = useState("all");
    const [dataFilter, setDataFilter] = useState([]);
    const [dataAccessories, setDataAccessories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [isMobile, setIsMobile] = useState(
        () => typeof window !== "undefined" && window.innerWidth <= 767
    );

    // Responsive screen detection
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 767);
        };

        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    // Fetch categories
    useEffect(() => {
        let active = true;

        const fetchCategories = async () => {
            try {
                const response = await axios.get(
                    `${API_BASE}/api/accessoriesCategory`
                );

                if (!active) return;

                const categories = response.data?.accessories_category;

                setDataFilter([
                    {
                        accessories_category_name_E: "All",
                        accessories_category_name_A: "الكل",
                        accessories_category_id: "all",
                    },
                    ...(Array.isArray(categories) ? categories : []),
                ]);
            } catch (error) {
                console.error("Error fetching categories:", error);

                if (active) {
                    setDataFilter([
                        {
                            accessories_category_name_E: "All",
                            accessories_category_name_A: "الكل",
                            accessories_category_id: "all",
                        },
                    ]);
                }
            }
        };

        fetchCategories();

        return () => {
            active = false;
        };
    }, []);

    // Fetch ALL accessories returned by the API
    useEffect(() => {
        let active = true;

        const fetchAccessories = async () => {
            try {
                setLoading(true);
                setError("");

                const response = await axios.get(
                    `${API_BASE}/api/accessories`
                );

                if (!active) return;

                const accessories = response.data?.accessories;

                const products = Array.isArray(accessories)
                    ? accessories
                    : Array.isArray(accessories?.data)
                    ? accessories.data
                    : [];

                setDataAccessories(products);

                console.log("Total Accessories:", products.length);
                console.log("Accessories API Response:", response.data);
            } catch (error) {
                console.error("Error fetching accessories:", error);

                if (active) {
                    setError(
                        isArabic
                            ? "حدث خطأ أثناء تحميل المنتجات"
                            : "Failed to load products"
                    );
                }
            } finally {
                if (active) {
                    setLoading(false);
                }
            }
        };

        fetchAccessories();

        return () => {
            active = false;
        };
    }, []);

    // Filter products without limiting their number
    const filteredData = useMemo(() => {
        if (filter === "all") {
            return dataAccessories;
        }

        return dataAccessories.filter(
            (item) =>
                String(item.accessories_category_id) === String(filter)
        );
    }, [dataAccessories, filter]);

    const getLocalizedText = (arabic, english) => {
        return (isArabic ? arabic || english : english || arabic) || "";
    };

    const getImageUrl = (image) => {
        if (!image) return "";

        if (/^https?:\/\//i.test(image)) {
            return image;
        }

        return `${API_BASE}/storage/app/public/${String(image).replace(/^\/+/, "")}`;
    };

    const renderProduct = (el) => {
        const price = Number(el.accessories_price) || 0;

        const discount = Math.min(
            100,
            Math.max(0, Number(el.accessories_price_discount) || 0)
        );

        const priceAfterDiscount = (
            price * (1 - discount / 100)
        ).toFixed(2);

        const hasDiscount = discount > 0;

        const inStock = Number(el.accessories_stock_available) > 0;

        const inventoryClass = inStock
            ? "text-[#18AB00]"
            : "text-red-500";

        const accessoriesName = getLocalizedText(
            el.accessories_name_A,
            el.accessories_name_E
        );

        const accessoriesDesc = getLocalizedText(
            el.accessories_desc_A,
            el.accessories_desc_E
        );

        const shortDesc =
            accessoriesDesc.length > 75
                ? accessoriesDesc.substring(0, 75) + "..."
                : accessoriesDesc;

        const productUrl = `/accessoriesdetails/${el.accessories_id}`;

        return (
            <div
                key={el.accessories_id}
                className="
                    w-[250px]
                    max-w-full
                    flex
                    flex-col
                    h-auto
                    bg-[#f6f6f6]
                    overflow-hidden
                    border
                    border-[#d1d1d1]
                    justify-between
                    rounded-[4px]
                "
            >
                <Link
                    to={productUrl}
                    className="flex flex-col flex-1"
                >
                    <div className="w-full h-[250px] relative">
                        <div
                            className="
                                w-full
                                h-full
                                border-b
                                border-b-[#1485a6]
                            "
                        >
                            <img
                                className="w-full h-full object-contain"
                                src={getImageUrl(el.accessories_main_img)}
                                alt={accessoriesName}
                                loading="lazy"
                            />
                        </div>

                        {hasDiscount && (
                            <div
                                className="
                                    w-10
                                    h-10
                                    bg-[#1485a6]
                                    rounded-full
                                    text-white
                                    font-bold
                                    flex
                                    items-center
                                    justify-center
                                    absolute
                                    top-[10px]
                                    right-[10px]
                                "
                            >
                                {discount}%
                            </div>
                        )}
                    </div>

                    <div className="p-[10px]">
                        <div
                            className="
                                text-[18px]
                                font-bold
                                text-center
                                capitalize
                            "
                        >
                            {accessoriesName}
                        </div>

                        <p
                            className="
                                text-[13px]
                                text-[#666666]
                                text-center
                                mt-2
                            "
                            dir={isArabic ? "rtl" : "ltr"}
                        >
                            {shortDesc}
                        </p>

                        <div
                            className="
                                flex
                                items-center
                                justify-center
                                flex-wrap
                                gap-[5px]
                                text-center
                                mt-2
                            "
                        >
                            <span className={`font-bold ${inventoryClass}`}>
                                {priceAfterDiscount} {t("JOD")}
                            </span>

                            {hasDiscount && (
                                <span className="line-through text-gray-500">
                                    {price.toFixed(2)} {t("JOD")}
                                </span>
                            )}
                        </div>

                        <div
                            className={`
                                text-center
                                font-bold
                                mt-2
                                ${inStock ? "text-[#18AB00]" : "text-red-500"}
                            `}
                        >
                            {t(inStock ? "In Stock" : "Out of Stock")}
                        </div>
                    </div>
                </Link>

                <Link
                    to={productUrl}
                    className="
                        bg-[#1485a6]
                        flex
                        p-1.5
                        items-center
                        justify-center
                        gap-1.5
                        text-white
                        font-bold
                        rounded-tl-[20px]
                        rounded-tr-[20px]
                        transition-opacity
                        duration-300
                        hover:opacity-90
                    "
                >
                    <CartProduct />
                    {t("Add To Cart")}
                </Link>
            </div>
        );
    };

    return (
        <>
            {/* Page Header */}
            <section className="landingPages">
                <div
                    className="
                        container
                        h-full
                        flex
                        flex-col
                        gap-[10px]
                        justify-center
                    "
                >
                    <div className="text-[35px] font-bold text-white">
                        {t("All Product")}
                    </div>

                    <div className="flex items-center gap-[3px] text-white">
                        <Link to="/">
                            {t("Home")}
                        </Link>

                        <span>-</span>

                        <div>
                            {t("All Product")}
                        </div>
                    </div>
                </div>
            </section>

            {/* Products Section */}
            <section className="relative py-[40px]">
                <div className="container">
                    <div className="flex flex-col gap-[15px]">

                        {/* Category Filters */}
                        <div>
                            <Swiper
                                slidesPerView="auto"
                                spaceBetween={10}
                                navigation={false}
                                loop={false}
                                dir="ltr"
                                style={{ direction: "ltr" }}
                            >
                                {dataFilter.map((item) => {
                                    const categoryId =
                                        item.accessories_category_id;

                                    const isActive =
                                        String(filter) === String(categoryId);

                                    return (
                                        <SwiperSlide
                                            key={categoryId}
                                            style={{ width: "fit-content" }}
                                        >
                                            <button
                                                type="button"
                                                className={`filter ${
                                                    isActive ? "active" : ""
                                                }`}
                                                onClick={() => {
                                                    setFilter(categoryId);
                                                }}
                                                style={{
                                                    padding: "10px 20px",
                                                    whiteSpace: "nowrap",
                                                    border: "1px solid #ddd",
                                                    borderRadius: "5px",
                                                }}
                                            >
                                                {getLocalizedText(
                                                    item.accessories_category_name_A,
                                                    item.accessories_category_name_E
                                                )}
                                            </button>
                                        </SwiperSlide>
                                    );
                                })}
                            </Swiper>
                        </div>

                        {/* Loading */}
                        {loading && (
                            <div className="text-center py-[50px] text-[18px]">
                                {isArabic
                                    ? "جاري تحميل المنتجات..."
                                    : "Loading products..."}
                            </div>
                        )}

                        {/* Error */}
                        {!loading && error && (
                            <div className="text-center py-[50px] text-red-500">
                                {error}
                            </div>
                        )}

                        {/* All Products - No Slice */}
                        {!loading && !error && (
                            <>
                                {filteredData.length > 0 ? (
                                    <div
                                        className="
                                            flex
                                            flex-wrap
                                            gap-[10px]
                                            justify-start
                                        "
                                    >
                                        {filteredData.map(renderProduct)}
                                    </div>
                                ) : (
                                    <div className="text-center py-[50px]">
                                        {isArabic
                                            ? "لا توجد منتجات في هذا التصنيف"
                                            : "No products found in this category"}
                                    </div>
                                )}
                            </>
                        )}

                    </div>
                </div>
            </section>
        </>
    );
}
